'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import styles from '../styles/Home.module.css';
import { TosCheckbox } from '../lib/TosAcceptance';
import UserDropdown from '../components/UserDropdown';

interface UserData {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  role: string;
}

export default function HomeClient() {
  const router = useRouter();
  const [roomName, setRoomName] = useState('');
  const [inviteInput, setInviteInput] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isCheckingInvite, setIsCheckingInvite] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    fetch('/api/auth/session', { cache: 'no-store' })
      .then(r => r.json())
      .then(data => {
        setIsAuthenticated(data.authenticated);
        if (data.authenticated && data.user) {
          setUser(data.user);
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const generateRoomName = () => {
    const adjectives = ['quick', 'bright', 'calm', 'bold', 'swift', 'keen', 'warm', 'cool'];
    const nouns = ['mesa', 'sala', 'nodo', 'link', 'sync', 'meet', 'hub', 'red'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const num = Math.floor(Math.random() * 1000);
    return `${adj}-${noun}-${num}`;
  };

  const handleCreateRoom = () => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }
    const room = roomName.trim() || generateRoomName();
    setIsJoining(true);
    router.push(`/rooms/${encodeURIComponent(room)}`);
  };

  const extractInviteCode = (input: string): string | null => {
    const trimmed = input.trim();

    // Try to extract code from full URL
    // Matches: https://meet.example.com/invite/CODE or /invite/CODE
    const urlMatch = trimmed.match(/\/invite\/([a-zA-Z0-9]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    // If it looks like just a code (alphanumeric, 6-20 chars typical)
    if (/^[a-zA-Z0-9]{6,20}$/.test(trimmed)) {
      return trimmed;
    }

    return null;
  };

  const handleJoinWithInvite = async () => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }

    setInviteError('');

    const code = extractInviteCode(inviteInput);
    if (!code) {
      setInviteError('Código de invitación inválido');
      return;
    }

    setIsCheckingInvite(true);

    try {
      // Verify invite exists and is valid
      const res = await fetch(`/api/invites/${code}`);
      const data = await res.json();

      if (!res.ok || !data.isValid) {
        if (data.isExpired) {
          setInviteError('Esta invitación ha expirado');
        } else if (data.isUsedUp) {
          setInviteError('Esta invitación ya fue utilizada');
        } else {
          setInviteError('Invitación no encontrada');
        }
        setIsCheckingInvite(false);
        return;
      }

      // Redirect to invite page
      router.push(`/invite/${code}`);
    } catch (error) {
      setInviteError('Error al verificar la invitación');
      setIsCheckingInvite(false);
    }
  };

  const handleInviteKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJoinWithInvite();
    }
  };

  const handleCreateKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateRoom();
    }
  };

  return (
    <div className={styles.main} data-lk-theme="default">
      {/* Top header with user menu */}
      <header className={styles.header} role="banner">
        <div className={styles.headerContent}>
          {isAuthenticated && user ? (
            <UserDropdown user={user} />
          ) : (
            <Link href="/login" className={styles.headerLink}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Ingresar
            </Link>
          )}
        </div>
      </header>

      <main className={styles.container}>
        {/* Left side - Hero */}
        <div className={styles.hero}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className={styles.logoText}>
              alter<span className={styles.logoTextAccent}>mundi</span>
            </span>
          </div>

          <h1 className={styles.title}>
            Reuniones seguras para comunidades
          </h1>

          <p className={styles.subtitle}>
            Conecta con tu comunidad a través de videollamadas de alta calidad,
            baja latencia y acceso controlado por invitación.
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <div className={styles.featureIcon}>✓</div>
              <span>Sin registro requerido para invitados</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureIcon}>✓</div>
              <span>Enlaces de invitación con permisos configurables</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureIcon}>✓</div>
              <span>Latencia ultra baja con WebRTC</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureIcon}>✓</div>
              <span>Código abierto y auto-hospedado</span>
            </div>
          </div>
        </div>

        {/* Right side - Action card */}
        <div className={styles.actionCard}>
          <div className={styles.card}>
            {/* Join with Invite Section */}
            <h2 className={styles.cardTitle}>Unirse con invitación</h2>
            <p className={styles.cardSubtitle}>
              Pega el enlace o código de invitación
            </p>

            <div className={styles.inviteSection}>
              <div className={styles.inputWithButton}>
                <input
                  type="text"
                  className={`${styles.roomInput} ${inviteError ? styles.inputError : ''}`}
                  placeholder="Enlace o código de invitación"
                  aria-label="Enlace o código de invitación"
                  value={inviteInput}
                  onChange={(e) => {
                    setInviteInput(e.target.value);
                    setInviteError('');
                  }}
                  onKeyPress={handleInviteKeyPress}
                  disabled={isCheckingInvite}
                />
                <button
                  className={styles.joinButton}
                  onClick={handleJoinWithInvite}
                  disabled={isCheckingInvite || !inviteInput.trim() || !tosAccepted}
                  aria-label="Unirse con invitación"
                >
                  {isCheckingInvite ? (
                    <span className={styles.spinner} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
              {inviteError && (
                <p className={styles.errorText}>{inviteError}</p>
              )}
            </div>

            <div className={styles.divider}>o crear reunión</div>

            {/* Create/Join Room Section */}
            <div className={styles.createSection}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  className={styles.roomInput}
                  placeholder="Nombre de sala (opcional)"
                  aria-label="Nombre de sala (opcional)"
                  title="Deja vacío para generar un nombre automático"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  onKeyPress={handleCreateKeyPress}
                  disabled={isJoining}
                />
              </div>

              <button
                className={styles.createButton}
                onClick={handleCreateRoom}
                disabled={isJoining || isLoading || !tosAccepted}
              >
                {isJoining ? (
                  <>
                    <span className={styles.spinner} />
                    Conectando...
                  </>
                ) : (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Crear Reunión
                  </>
                )}
              </button>
            </div>

            {/* TOS Acceptance */}
            <TosCheckbox onAcceptChange={setTosAccepted} />
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        Powered by <a href="https://livekit.io" target="_blank" rel="noopener noreferrer">LiveKit</a>
        {' '}&bull;{' '}
        <a href="https://altermundi.net" target="_blank" rel="noopener noreferrer">AlterMundi</a>
        {' '}&bull;{' '}
        <a href="/legal">Aviso Legal</a>
      </footer>
    </div>
  );
}
